import React, { useState, useEffect } from 'react';
import { Database, Plus, Search, Filter, FileText, Link as LinkIcon, Upload, X, Check, Clock, XCircle, Trash2, FileUp, UploadCloud, FileSpreadsheet , Image as ImageIcon, Video, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

export default function CorpusCollection() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importMode, setImportMode] = useState('FILE'); // 'FILE' or 'URL'
  const [urlInput, setUrlInput] = useState('');
  const [isUrlImporting, setIsUrlImporting] = useState(false);
  const [importType, setImportType] = useState('TEXT'); // TEXT, LINK, DOC, EXCEL
  
  // Import Form State
  const [importTitle, setImportTitle] = useState('');
  const [importContent, setImportContent] = useState('');
  const [importUrl, setImportUrl] = useState('');
  const [importFiles, setImportFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editIsSubmitting, setEditIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('DOCUMENT'); // 'DOCUMENT' or 'MEDIA'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // for the input field to avoid fetching on every keystroke
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchItems();
    window.addEventListener('globalFilterChange', fetchItems);
    return () => window.removeEventListener('globalFilterChange', fetchItems);
  }, [currentPage, activeTab, searchQuery, statusFilter]);

  // Reset page when tab/search/filter changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [activeTab, searchQuery, statusFilter]);

  const fetchItems = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      setItems([]);
      setTotalItems(0);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const sourceTypes = activeTab === 'MEDIA' ? 'IMAGE,VIDEO,MEDIA' : 'TEXT,LINK,DOCUMENT,DOC,FILE,EXCEL';
      let url = `/api/corpus/items?projectId=${projectId}&sourceTypeExclude=ECO_LINK&page=${currentPage}&pageSize=${pageSize}&sourceTypes=${sourceTypes}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const res = await axios.get(url);
      setItems(res.data.items || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch corpus items:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setImportFiles(Array.from(e.target.files));
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      return alert('请先在顶部导航栏选择一个具体的品牌项目');
    }
    
    if (importType === 'TEXT' && (!importTitle || !importContent)) return alert('请完整填写标题和内容');
    if (importType === 'LINK' && !importUrl) return alert('请输入URL');
    if (importType === 'DOC' && importFiles.length === 0) return alert('请至少选择一个文档');
    if (importType === 'EXCEL' && importFiles.length === 0) return alert('请选择 Excel 文件');
    if (importType === 'MEDIA' && importFiles.length === 0) return alert('请选择图片或视频文件');

    setIsSubmitting(true);
    
    try {
      if (importType === 'DOC' || importType === 'EXCEL' || importType === 'MEDIA') {
        const formData = new FormData();
        formData.append('projectId', projectId);
        formData.append('sourceType', importType);
        formData.append('creatorId', user?.userId || user?.id || 1);
        importFiles.forEach(file => {
          formData.append('files', file);
        });

        await axios.post('/api/corpus/upload-batch', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        if (importType === 'LINK') {
          const urls = importUrl.split('\n').map(u => u.trim()).filter(Boolean);
          if (urls.length === 0) return alert('请输入有效的URL链接');
          if (urls.length > 50) return alert('一次最多导入 50 条链接');
          
          await axios.post('/api/corpus/upload-urls', {
            projectId: projectId,
            urls: urls,
            creatorId: user?.userId || user?.id
          });
        } else {
          // 单条上传 TEXT
          await axios.post('/api/corpus/items', {
            projectId: projectId,
            title: importTitle,
            sourceType: importType,
            rawContent: importContent,
            sourceUrl: null,
            creatorId: user?.userId || user?.id
          });
        }
      }
      
      setIsImportModalOpen(false);
      resetForm();
      fetchItems();
    } catch (err) {
      console.error(err);
      alert('导入失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  
  
  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`确定要删除选中的 ${selectedIds.length} 项资产吗？\n此操作不可恢复。`)) return;
    
    try {
      await axios.delete('/api/corpus/items/batch', { data: { ids: selectedIds } });
      setItems(items.filter(i => !selectedIds.includes(i.id)));
      setSelectedIds([]);
      alert('批量删除成功');
    } catch(err) {
      console.error(err);
      alert('批量删除失败');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredItems.map(i => i.id));
    }
  };

  const handleDelete = async (item) => {
    if (!confirm('确定删除这条基础语料吗？')) return;
    try {
      await axios.delete('/api/corpus/items?id=' + item.id);
      setItems(items.filter(i => i.id !== item.id));
    } catch (err) {
      console.error(err);
      alert('删除失败');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setEditTitle(item.title || '');
    setEditContent(item.rawContent || item.content || '');
    setEditUrl(item.sourceUrl || item.url || '');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTitle) return alert('标题不能为空');
    try {
      setEditIsSubmitting(true);
      await axios.patch(`/api/corpus/items/${editingItem.id}`, {
        title: editTitle,
        rawContent: editContent,
        sourceUrl: editUrl,
      });
      setEditingItem(null);
      fetchItems();
    } catch (err) {
      console.error(err);
      alert('编辑失败');
    } finally {
      setEditIsSubmitting(false);
    }
  };
  
const resetForm = () => {
    setImportTitle('');
    setImportContent('');
    setImportUrl('');
    setImportFiles([]);
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.patch(`/api/corpus/items/${id}`, { 
        status,
        reviewerId: user?.userId || user?.id,
        
      });
      fetchItems();
    } catch (err) {
      console.error(err);
      alert('状态更新失败');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PENDING': return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300 rounded-full text-xs flex items-center gap-1 w-fit"><Clock className="w-3 h-3"/> 待处理</span>;
      case 'APPROVED': return <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300 rounded-full text-xs flex items-center gap-1 w-fit"><Check className="w-3 h-3"/> 已采纳</span>;
      case 'REJECTED': return <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300 rounded-full text-xs flex items-center gap-1 w-fit"><XCircle className="w-3 h-3"/> 已驳回</span>;
      default: return null;
    }
  };

  // Backend does the filtering now
  const filteredItems = items;
  
  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-500" />
            基础语料池
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            导入文本、文档或抓取网页，清洗后进入待结构化队列。
          </p>
        </div>
        {(user?.role === 'ADMIN' || user?.permissions?.includes('corpus:edit')) && (
          <button 
            onClick={() => { resetForm(); setIsImportModalOpen(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            导入原始语料
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6">
        <div className="flex border-b border-gray-200 dark:border-slate-700 mb-6">
          <button 
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'DOCUMENT' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('DOCUMENT')}
          >
            <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> 文本与文档</span>
          </button>
          <button 
            className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === 'MEDIA' ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('MEDIA')}
          >
            <span className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> 视觉与视频物料</span>
          </button>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="搜索语料标题或原始正文 (按回车搜索)..." 
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearchQuery(searchInput); }}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
            />
          </div>
          <button onClick={toggleSelectAll} className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium whitespace-nowrap">
            {selectedIds.length === filteredItems.length && filteredItems.length > 0 ? '取消全选' : '全选本页'}
          </button>
          {selectedIds.length > 0 && (
            <button onClick={handleBatchDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center text-sm font-medium whitespace-nowrap">
              <Trash2 className="w-4 h-4 mr-2" />
              批量删除 ({selectedIds.length})
            </button>
          )}
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="py-2 pl-3 pr-8 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
          >
            <option value="">全/过滤状态</option>
            <option value="PENDING">待处理</option>
            <option value="APPROVED">已采纳</option>
            <option value="REJECTED">已驳回</option>
          </select>
        </div>
        
        {loading ? (
          <div className="text-center py-12 text-gray-500">加载数据中...</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-slate-400">目前暂无导入的语料记录。</p>
          </div>
        ) : activeTab === 'MEDIA' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredItems.map(item => (
              <div key={item.id} className={`bg-white dark:bg-slate-800 rounded-xl border overflow-hidden hover:shadow-lg transition-all group relative flex flex-col ${selectedIds.includes(item.id) ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 dark:border-slate-700'}`}>
                <div className="absolute top-2 left-2 z-10">
                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500 bg-white/80 border-gray-300 cursor-pointer shadow-sm" />
                  </div>
                  <div 
                  className="w-full aspect-video bg-gray-100 dark:bg-slate-900 cursor-pointer relative"
                  onClick={() => setPreviewMedia(item)}
                >
                  {item.sourceType === 'IMAGE' ? (
                    <img src={item.sourceUrl} alt="media" className="w-full h-full object-cover relative z-0" onError={(e) => { e.target.onerror = null; e.target.src = "/assets/fallback-image.png"; }} style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)" }} />
                  ) : (
                    <video src={item.sourceUrl} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none" style={{ WebkitTransform: "translateZ(0)", transform: "translateZ(0)" }}>
                    <Search className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                  </div>
                </div>
                <div className="p-3 flex-1 flex flex-col justify-between border-t border-gray-100 dark:border-slate-700/50">
                  <div className="flex justify-between items-center mb-2">
                    {getStatusBadge(item.status)}
                    <span className="text-[10px] text-gray-400">{new Date(item.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-end gap-2">
                    {item.status === 'PENDING' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit') || user?.permissions?.includes('corpus:edit')) && (
                      <button onClick={() => handleUpdateStatus(item.id, 'APPROVED')} className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-500/20 rounded text-xs font-medium">过审</button>
                    )}
                    {item.status !== 'REJECTED' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit') || user?.permissions?.includes('corpus:edit')) && (
                      <button onClick={() => handleUpdateStatus(item.id, 'REJECTED')} className="px-2 py-1 bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-500/20 rounded text-xs font-medium border border-yellow-200 dark:border-yellow-500/20">驳回</button>
                    )}
                    {(user?.role === 'ADMIN' || user?.permissions?.includes('corpus:edit')) && (
                      <button onClick={() => handleDelete(item)} className="px-2 py-1 bg-gray-50 dark:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-500/20 hover:text-red-500 dark:hover:text-red-400 rounded text-xs font-medium transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400">
                  <th className="w-12 pb-3 font-medium text-center pl-2">
                    <input 
                      type="checkbox" 
                      checked={selectedIds.length === filteredItems.length && filteredItems.length > 0} 
                      onChange={toggleSelectAll} 
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                  </th>
                  <th className="pb-3 font-medium pl-2">标题</th>
                  <th className="pb-3 font-medium">来源类型</th>
                  <th className="pb-3 font-medium">录入人</th>
                  <th className="pb-3 font-medium">录入时间</th>
                  <th className="pb-3 font-medium">处理状态</th>
                  <th className="pb-3 font-medium text-right pr-2">管理操作</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredItems.map(item => (
                  <tr key={item.id} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-gray-50 dark:hover:bg-slate-800/50 group transition-colors">
                    <td className="w-12 py-4 text-center pl-2">
                      <input 
                        type="checkbox" 
                        checked={selectedIds.includes(item.id)} 
                        onChange={() => toggleSelect(item.id)} 
                        className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                    </td>
                    <td className="py-4 pl-2 text-gray-900 dark:text-gray-100 font-medium">
                      {item.sourceType === 'IMAGE' || item.sourceType === 'VIDEO' ? (
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-24 h-16 bg-gray-100 dark:bg-slate-800 rounded overflow-hidden flex-shrink-0 border border-gray-200 dark:border-slate-700 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all"
                            onClick={() => setPreviewMedia(item)}
                            title="点击预览大图"
                          >
                            {item.sourceType === 'IMAGE' ? (
                              <img src={item.sourceUrl} alt={item.title} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = "/assets/fallback-image.png"; console.log("Failed to load image:", item.sourceUrl); }} />
                            ) : (
                              <video src={item.sourceUrl} className="w-full h-full object-cover" />
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="truncate max-w-xs" title={item.title}>{item.title}</div>
                          {item.sourceType === 'LINK' && (
                             <div className="text-xs text-blue-500 truncate max-w-xs mt-1" title={item.sourceUrl}>{item.sourceUrl}</div>
                          )}
                          {item.sourceType === 'DOC' && (
                             <div className="text-xs text-purple-500 truncate max-w-xs mt-1" title={item.rawContent}>{item.rawContent?.replace('[系统等待文档解析引擎处理: ', '')?.replace(']', '')}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-4 text-gray-500 dark:text-gray-400">
                      {item.sourceType === 'TEXT' ? (
                        <span className="flex items-center gap-1"><FileText className="w-4 h-4 text-gray-400"/> 纯文本</span>
                      ) : item.sourceType === 'LINK' ? (
                        <span className="flex items-center gap-1"><LinkIcon className="w-4 h-4 text-gray-400"/> 网页链接</span>
                      ) : item.sourceType === 'IMAGE' ? (
                        <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4 text-gray-400"/> 图片视觉</span>
                      ) : item.sourceType === 'VIDEO' ? (
                        <span className="flex items-center gap-1"><Video className="w-4 h-4 text-gray-400"/> 视频素材</span>
                      ) : (
                        <span className="flex items-center gap-1"><FileUp className="w-4 h-4 text-gray-400"/> 批量文档</span>
                      )}
                    </td>
                    <td className="py-4 text-gray-500 dark:text-gray-400">{item.creator?.name || '管理员'}</td>
                    <td className="py-4 text-gray-500 dark:text-gray-400">{new Date(item.createdAt).toLocaleString()}</td>
                    <td className="py-4">{getStatusBadge(item.status)}</td>
                    <td className="py-4 pr-2 text-right">
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {item.status === 'PENDING' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit') || user?.permissions?.includes('corpus:edit')) && (
                          <button 
                            onClick={() => handleUpdateStatus(item.id, 'APPROVED')}
                            className="px-3 py-1 bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-500/20 rounded border border-green-200 dark:border-green-500/20 text-xs font-medium shadow-sm transition-all"
                          >
                            过审入库
                          </button>
                        )}
                        {item.status !== 'REJECTED' && (user?.role === 'ADMIN' || user?.permissions?.includes('corpus:audit') || user?.permissions?.includes('corpus:edit')) && (
                          <button 
                            onClick={() => handleUpdateStatus(item.id, 'REJECTED')}
                            className="px-3 py-1 bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-500/20 rounded border border-yellow-200 dark:border-yellow-500/20 text-xs font-medium shadow-sm transition-all"
                          >
                            驳回
                          </button>
                        )}
                        {(user?.role === 'ADMIN' || user?.permissions?.includes('corpus:edit')) && (
                          <>
                            <button onClick={() => handleEdit(item)} className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-500/20 transition-colors">
                               <FileText className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(item)} className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-500/20 transition-colors">
                               <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {!loading && totalItems > 0 && (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-slate-700 mt-4 pt-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              共 {totalItems} 条记录
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {currentPage} / {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-gray-200 dark:border-slate-700 rounded-lg text-sm disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>


      {/* 编辑语料 Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100 dark:border-slate-700">
            <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/80">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-500" />
                编辑基础语料
              </h3>
              <button onClick={() => setEditingItem(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="p-5 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">标题 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="如: 小米SU7 核心卖点文档"
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg p-2.5 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                />
              </div>

              {editingItem.sourceType === 'LINK' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">原文链接</label>
                  <input
                    type="url"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full border border-gray-300 dark:border-slate-600 rounded-lg p-2.5 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">语料内容</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="输入或粘贴文本内容..."
                  rows="12"
                  className="w-full border border-gray-300 dark:border-slate-600 rounded-lg p-2.5 text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-mono"
                ></textarea>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-slate-700 mt-6 gap-3">
                <button
                  type="button"
                  onClick={() => setEditingItem(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={editIsSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center"
                >
                  {editIsSubmitting ? (
                    <><Upload className="w-4 h-4 mr-2 animate-bounce" /> 保存中...</>
                  ) : (
                    '保存修改'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 导入语料 Modal */}
      {previewMedia && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4" onClick={() => setPreviewMedia(null)}>
          <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setPreviewMedia(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2"
            >
              <X className="w-8 h-8" />
            </button>
            {previewMedia.sourceType === 'IMAGE' ? (
              <img src={previewMedia.sourceUrl} alt="Preview" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            ) : (
              <video src={previewMedia.sourceUrl} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg shadow-2xl" />
            )}
          </div>
        </div>
      )}

      {isImportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100 dark:border-slate-700">
            <div className="p-5 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/80">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                录入新语料
              </h2>
              <button onClick={() => setIsImportModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              <form onSubmit={handleImport} className="space-y-6" style={{ minHeight: "450px" }}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">选择输入方式</label>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={() => setImportType('TEXT')}
                      className={`py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${importType === 'TEXT' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 shadow-sm' : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}
                    >
                      <FileText className={`w-5 h-5 ${importType === 'TEXT' ? 'text-blue-500' : ''}`} />
                      <span className="font-medium text-xs">纯文本 (单条)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportType('LINK')}
                      className={`py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${importType === 'LINK' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 shadow-sm' : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}
                    >
                      <LinkIcon className={`w-5 h-5 ${importType === 'LINK' ? 'text-blue-500' : ''}`} />
                      <span className="font-medium text-xs">网页链接 (批量)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportType('DOC')}
                      className={`py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${importType === 'DOC' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 shadow-sm' : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}
                    >
                      <FileUp className={`w-5 h-5 ${importType === 'DOC' ? 'text-blue-500' : ''}`} />
                      <span className="font-medium text-xs">文档上传 (批量)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportType('EXCEL')}
                      className={`py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${importType === 'EXCEL' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 shadow-sm' : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}
                    >
                      <FileSpreadsheet className={`w-5 h-5 ${importType === 'EXCEL' ? 'text-blue-500' : ''}`} />
                      <span className="font-medium text-xs">Excel 导入 (批量)</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportType('MEDIA')}
                      className={`py-3 px-2 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${importType === 'MEDIA' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 shadow-sm' : 'border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50 text-gray-500 dark:text-gray-400 hover:border-gray-300'}`}
                    >
                      <ImageIcon className={`w-5 h-5 ${importType === 'MEDIA' ? 'text-blue-500' : ''}`} />
                      <span className="font-medium text-xs">图片/视频 (画廊)</span>
                    </button>
                  </div>
                </div>

                {/* 批量上传文件区域 (DOC / EXCEL) */}
                {(importType === 'DOC' || importType === 'EXCEL' || importType === 'MEDIA') && (
                  <div>
                    <div className="flex justify-between items-end mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        上传本地{importType === 'EXCEL' ? '表格' : (importType === 'MEDIA' ? '视觉资产(图片/视频)' : '文档')} <span className="text-red-500">*</span>
                      </label>
                      {importType === 'EXCEL' && (
                        <a href="/NexGEO_Corpus_Import_Template.xlsx" download className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                          <Download className="w-3 h-3" /> 下载标准导入模板 (.xlsx)
                        </a>
                      )}
                    </div>
                    <div className="bg-gray-50 dark:bg-slate-900 p-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 text-center hover:border-blue-500 transition-colors relative">
                      <UploadCloud className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      
                      {importFiles.length > 0 ? (
                        <div className="max-h-32 overflow-y-auto w-3/4 mx-auto border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 p-2 text-left z-10 relative">
                           <p className="text-xs font-bold text-blue-600 dark:text-blue-400 mb-2 border-b pb-1">已选择 {importFiles.length} 个文件:</p>
                           <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1.5">
                             {importFiles.map((f, i) => (
                               <li key={i} className="flex items-center gap-2 truncate">
                                 <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0"/> 
                                 <span className="truncate">{f.name}</span>
                               </li>
                             ))}
                           </ul>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">点击或将多个文件拖拽到此处</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {importType === 'EXCEL' ? '支持 .xlsx, .csv 格式表格' : (importType === 'MEDIA' ? '支持 JPG, PNG, WEBP, MP4, MOV 格式，支持拖拽多文件' : '支持 PDF, Word (.docx), TXT 格式，支持框选多个文件')}
                          </p>
                        </>
                      )}
                      <input 
                        type="file" 
                        multiple={importType === 'DOC' || importType === 'MEDIA'} // Excel 通常单次传一张表
                        accept={importType === 'EXCEL' ? '.xlsx,.csv' : (importType === 'MEDIA' ? 'image/*,video/*' : '.pdf,.docx,.txt')}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-0"
                        onChange={handleFileUpload}
                      />
                    </div>
                  </div>
                )}

                {/* 单条上传标题区域 (TEXT / LINK) */}
                {(importType === 'TEXT') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">语料标题 <span className="text-red-500">*</span></label>
                    <input 
                      type="text" 
                      value={importTitle}
                      onChange={(e) => setImportTitle(e.target.value)}
                      placeholder="例如：2026款旗舰版首测反馈"
                      className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 dark:text-white transition-shadow"
                      required
                    />
                  </div>
                )}

                {importType === 'TEXT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">生肉正文 <span className="text-red-500">*</span></label>
                    <textarea 
                      value={importContent}
                      onChange={(e) => setImportContent(e.target.value)}
                      placeholder="请将原始文章或客户反馈完整粘贴在此处..."
                      rows={6}
                      className="w-full p-3 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 dark:text-white resize-y transition-shadow"
                      required
                    />
                  </div>
                )}

                {importType === 'LINK' && (
                  <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">目标链接列表 (每行一个) <span className="text-red-500">*</span></label>
                    <p className="text-xs text-gray-500 mb-2">支持批量输入文章、新闻、官网等链接。系统会自动在后台抓取网页正文并提炼结构化卖点。</p>
                    <textarea 
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://example.com/article-1
https://example.com/article-2"
                      rows={6}
                      className="w-full p-2.5 border border-gray-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-gray-900 dark:text-white transition-shadow mb-2"
                      required
                    />
                  </div>
                )}
              </form>
            </div>
            
            <div className="p-5 border-t border-gray-100 dark:border-slate-700 flex justify-end gap-3 bg-white dark:bg-slate-800">
              <button 
                type="button" 
                onClick={() => setIsImportModalOpen(false)}
                className="px-5 py-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
              >
                取消
              </button>
              <button 
                onClick={handleImport}
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm transition-all active:scale-95"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    处理中...
                  </span>
                ) : (
                  <>确认并入库</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
